/**
 * Custom hook for managing PO Lines Tab state and operations.
 * Composes sub-hooks for bulk actions, export, and workflow actions.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { deliveredPercentOf } from '../../deliveredQty';
import { useSearchParams } from 'react-router-dom';
import { purchasingAPI, PurchaseOrderLine } from '../../../../lib/api';
import { getApiErrorMessage } from '../../../../lib/api/types/common';
import { Cache } from '../../../../lib/cache';
import { useAuth } from '../../../../contexts/AuthContext';
import { useView } from '../../../../contexts/ViewContext';
import {
  ColumnConfig,
  DEFAULT_COLUMNS,
  COLUMN_STORAGE_KEY,
  SUPPLIER_RESTRICTED_COLUMNS,
  SUPPLIER_ONLY_COLUMNS,
  CALL_OFF_SUPPLY_COLUMNS,
  POLinesFilters,
  DEFAULT_FILTERS,
  FilterOptions,
} from './types';
import { buyerIdFilterParam } from './filters/BuyerIdMultiSelect';
import { isOtifDecisionFilter } from '../../otifDecision';
import { usePOLinesBulkActions } from './usePOLinesBulkActions';
import { usePOLinesExport } from './usePOLinesExport';
import { usePOLinesActions } from './usePOLinesActions';
import { usePOLinesSupplierScope, ALL_SUPPLIERS_PARAM } from './usePOLinesSupplierScope';
import { OPEN_LINE_STATUSES, dueWindowParams } from './dueWindowParams';
import { useMenuSettings } from '../../../../contexts/MenuSettingsContext';
import { pageCountFromResponse, type PageSizeMemory } from '../../../../hooks/usePagination';
import { sortRows } from '../../../../hooks/useColumnSort';

// Columns hidden from PO Lines when schedule_collaboration is enabled
// (these are managed at the schedule level instead).
// EXPEDITING-PARITY PR 2 (17 Sep 2026): 'on_hold' is no longer one of them
// because the column no longer exists — a held line's status IS the hold, so
// 'acknowledge_status' above already hides it. Left in, the id would be a dead
// entry that reads as coverage.
const SCHEDULE_COLLAB_HIDDEN_COLUMNS = [
  'acknowledge_status', 'action', 'next_action', 'hold_reason',
];

const DEFAULT_PAGE_SIZE = 50;

/**
 * Column id -> the queryset path the API can order by.
 *
 * Only columns backed by a real field belong here. Anything computed in the
 * serializer (pct_delivered, next_action, the converted_* currency values, the
 * contract prices, the change counts) has nothing for the database to sort on,
 * so it stays with the client-side comparator further down and orders only the
 * loaded page. Adding one of those here would silently sort by nothing.
 *
 * Keep in step with `ordering_fields` on PurchaseOrderLineViewSet — a value the
 * viewset does not allow is ignored by DRF, which looks like sorting quietly
 * not working.
 */
const SERVER_SORTABLE: Record<string, string> = {
  po_number: 'po__po_number',
  customer_name: 'po__customer__name',
  supplier_name: 'po__supplier__name',
  supplier_group_name: 'po__supplier__group__name',
  line_number: 'line_number',
  part_number: 'part_number',
  description: 'description',
  quantity: 'quantity',
  // CR-7417: the Qty Accepted column is NOT here. It shows the accepted
  // quantity, which is derived, and the server cannot order by it in a time the
  // page can wait for (23.9s measured on 96,097 lines, against a 0.8-2.3s
  // baseline). Keeping the old `received_qty` entry was the alternative and is
  // worse than having no sort: this diff moves the DISPLAY to the accepted
  // figure, so that entry would now order the grid by a number that is not on
  // the screen. It was matched before — same field shown and sorted — and this
  // change is what would have broken it.
  outstanding_qty: 'outstanding_qty',
  unit_price: 'unit_price',
  total_price: 'total_price',
  // DATE-FIELD-CANON PR 2: delivery_date is the Customer Required Date
  // column again; the ODD column has its own id and sorts on the server's
  // computed odd_date.
  delivery_date: 'delivery_date',
  odd_date: 'odd_date',
  // CR-7333: the Drawing Issue column (id kept as drawing_version so saved
  // layouts survive) shows the feed's drawing_issue, so it sorts on
  // drawing_issue. Without an entry here the column sorted only the loaded
  // page, because DRF drops an ordering value the viewset does not list and
  // this map is what sends one at all.
  drawing_version: 'drawing_issue',
  supplier_promise_date: 'supplier_promise_date',
  // CR-7743: days left counts down to the promise date, so it orders by it.
  days_left_to_produce: 'supplier_promise_date',
  acknowledge_status: 'acknowledge_status',
  on_hold: 'on_hold',
  buyer_id: 'buyer_id',
  batch_serial_no: 'batch_serial_no',
  ppv: 'ppv',
  is_critical: 'is_critical',
  order_date: 'po__issue_date',
};

/**
 * URL params that survive clearing the consumed deep-link filters: the
 * Purchasing tab, and the CR-9095 supplier scope (not a filter).
 *
 * @param searchParams - the current URL params
 * @returns the params to keep
 */
function keptUrlParams(searchParams: URLSearchParams): Record<string, string> {
  const kept: Record<string, string> = {};
  const tab = searchParams.get('tab');
  if (tab) kept['tab'] = tab;
  if (searchParams.get(ALL_SUPPLIERS_PARAM) === 'true') kept[ALL_SUPPLIERS_PARAM] = 'true';
  return kept;
}

export function usePOLinesState() {
  const { user } = useAuth();
  const { getViewQueryParams, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, isLoading: viewContextLoading } = useView();
  const { isFeatureEnabled } = useMenuSettings();
  const scheduleCollabEnabled = isFeatureEnabled('schedule_collaboration');
  // Per-supplier flag: surfaces SupplierCustomerRelationship.call_off_enabled.
  // Drives visibility of the warehouse-stock column on the PO Lines table —
  // suppliers who don't run call-off see no warehouse-allocation concept.
  const callOffEnabled = isFeatureEnabled('switch_call_off_tab');
  const [searchParams, setSearchParams] = useSearchParams();

  // Data state
  const [lines, setLines] = useState<PurchaseOrderLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const servedPageRef = useRef<PageSizeMemory | undefined>(undefined);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // CR-4695 rework: Default to 'open' so closed lines aren't shown by default
  const [activeTab, setActiveTab] = useState('open');
  const [showFilters, setShowFilters] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [searchTrigger, setSearchTrigger] = useState(0);
  // CR-9095: My suppliers / All suppliers, as on PO Headers and Deliveries.
  // A change returns to page 1 and re-queries the grid; the counts follow
  // buildFilterParams.
  const handleScopeChange = useCallback(() => {
    setCurrentPage(1);
    setSearchTrigger(prev => prev + 1);
  }, []);
  const supplierScope = usePOLinesSupplierScope({ onScopeChange: handleScopeChange });
  const { listScopeParams, exportScopeParams } = supplierScope;
  // The OTIF Review pill opens on Not Decided — the lines still owing a
  // decision — rather than every line ever reviewed. Once the user (or a deep
  // link) picks a decision, including All, that choice stands.
  const otifDecisionChosenRef = useRef(false);

  // Selection state
  const [selectedLineId, setSelectedLineId] = useState<number | null>(null);
  const [selectedLineAction, setSelectedLineAction] = useState<'propose_change' | null>(null);

  // Sort state
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Tab badge counts
  const [needsActionCount, setNeedsActionCount] = useState<number | null>(null);
  // CR-7418: the OTIF Review queue now lives on this grid rather than on
  // Supplier Performance, where it was built by mistake — legacy has always
  // carried it as a sub-tab of the PO line grid, with the decision taken in
  // the grid itself.
  const [otifReviewCount, setOtifReviewCount] = useState<number | null>(null);
  const [openCount, setOpenCount] = useState<number | null>(null);
  const [closedCount, setClosedCount] = useState<number | null>(null);
  const [onHoldCount, setOnHoldCount] = useState<number | null>(null);
  const [duePreviousWeeksCount, setDuePreviousWeeksCount] = useState<number | null>(null);
  const [dueThisWeekCount, setDueThisWeekCount] = useState<number | null>(null);
  const [dueNextWeekCount, setDueNextWeekCount] = useState<number | null>(null);
  // True while the batched count request is in flight. The numbers stay on
  // screen but pulse, so a stale value is recognisable as refreshing rather
  // than being mistaken for the current answer.
  const [countsRefreshing, setCountsRefreshing] = useState(false);
  // Always-visible quick search — one debounced term matched server-side
  // against part number, supplier part reference or PO number (part_search).
  const [partSearch, setPartSearch] = useState('');
  // The term the grid and badges were last asked for. It only moves when the
  // debounce fires, so the list query and the count query always carry the
  // same term — and fetchLines re-memoises on it, so the search trigger runs a
  // fresh query instead of replaying the previous URL (which the browser then
  // served from its HTTP cache, leaving the grid untouched).
  const [appliedPartSearch, setAppliedPartSearch] = useState('');
  const partSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Filter options (CR-3413)
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);

  // Filter state
  const [filters, setFilters] = useState<POLinesFilters>(DEFAULT_FILTERS);

  // Column visibility and order state (CR-3406, CR-3846)
  // When schedule_collaboration is enabled, hide ack/action columns (managed at schedule level)
  // CR-4716: Supplier users cannot see commercial/internal columns at all
  const isSupplierUser = user?.platform === 'SUPPLIER';
  const [columns, setColumns] = useState<ColumnConfig[]>(() => {
    const hideCollab = (cols: ColumnConfig[]) =>
      scheduleCollabEnabled
        ? cols.map(c => SCHEDULE_COLLAB_HIDDEN_COLUMNS.includes(c.id) ? { ...c, visible: false } : c)
        : cols;

    // CR-4716: Remove supplier-restricted columns entirely for supplier users
    const stripRestricted = (cols: ColumnConfig[]) =>
      isSupplierUser ? cols.filter(c => !SUPPLIER_RESTRICTED_COLUMNS.includes(c.id)) : cols;

    // "Delivered into Warehouse" is shown to both sides of a call-off
    // relationship: the supplier reporting the stock, and the customer waiting
    // on it. switch_call_off_tab is already derived from the relationship from
    // either direction, so the flag alone is the correct gate — a customer with
    // no call-off supplier still sees nothing. The supplier-side closure
    // behaviour (grey-out, "In Stock" badge, action suppression) stays
    // supplier-only and is gated separately at its own call sites.
    const stripCallOffOnly = (cols: ColumnConfig[]) =>
      callOffEnabled
        ? cols
        : cols.filter(
            c => !CALL_OFF_SUPPLY_COLUMNS.includes(c.id)
              && !SUPPLIER_ONLY_COLUMNS.includes(c.id),
          );

    // CR-7163 (a): Fulfilment % is a supplier production metric — the ticket
    // asks for it "only for the supplier side". Removed entirely (not just
    // hidden) for everyone else, so it cannot be re-enabled from the column
    // picker.
    const stripSupplierOnly = (cols: ColumnConfig[]) =>
      isSupplierUser ? cols : cols.filter(c => !SUPPLIER_ONLY_COLUMNS.includes(c.id));

    const saved = localStorage.getItem(COLUMN_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const merged = DEFAULT_COLUMNS.map(defaultCol => {
          const savedCol = parsed.find((c: ColumnConfig) => c.id === defaultCol.id);
          return savedCol
            ? { ...defaultCol, visible: savedCol.visible, order: savedCol.order ?? defaultCol.order }
            : defaultCol;
        }).sort((a, b) => a.order - b.order);
        return stripSupplierOnly(stripCallOffOnly(stripRestricted(hideCollab(merged))));
      } catch {
        return stripSupplierOnly(stripCallOffOnly(stripRestricted(hideCollab(DEFAULT_COLUMNS))));
      }
    }
    return stripSupplierOnly(stripCallOffOnly(stripRestricted(hideCollab(DEFAULT_COLUMNS))));
  });
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const columnDropdownRef = useRef<HTMLDivElement>(null);

  // Track whether the next fetch should bust the browser HTTP cache.
  const bustCacheRef = useRef(false);

  // CR-3383: Check for PO number and line number filter from URL params
  // Wait for ViewContext to finish loading before marking ready
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) return;
    if (viewContextLoading) return;
    initializedRef.current = true;

    const urlPoFilter = searchParams.get('poFilter');
    const urlLineFilter = searchParams.get('lineFilter');
    const urlSearch = searchParams.get('search');
    // CR-6176: part number passed from the Stock Planning grid (clickable
    // part number) to show every PO line for that part.
    const urlPartNumber = searchParams.get('partNumber');
    // CR-4667: Supplier id passed from OTIF drill-down to disambiguate
    // POs whose number collides across suppliers.
    const urlSupplier = searchParams.get('supplier');
    // CR-5766: Status tab param to control which status tab to show (open/closed/all)
    const urlStatusTab = searchParams.get('status_tab');
    // CR-6310: Date range from Performance scorecard drill-through
    const urlDateFrom = searchParams.get('date_from');
    const urlDateTo = searchParams.get('date_to');
    // Daily-digest deep links: pre-select the ODD-status / line-status
    // filter so the page shows the same population the emailed count did.
    const urlOddStatus = searchParams.get('odd_status');
    const urlLineStatus = searchParams.get('line_status');
    // CR-6457: the supplier's Weekly Arrears Report links a count to one
    // customer (by id), the counterpart of ?supplier= on the customer side.
    const urlCustomer = searchParams.get('customer');
    const customerDeepLink = urlCustomer && /^\d+$/.test(urlCustomer) ? urlCustomer : null;

    // Preserve the tab param (and the CR-9095 supplier scope, which is not a
    // filter) when clearing consumed deep-link filters
    const preserveTab = () => {
      setSearchParams(keptUrlParams(searchParams), { replace: true });
    };

    // CR-7260: `?line=N` opens the line's modal (POLinesTab) but never touched
    // this list, so closing the modal dumped the user in the whole order book.
    // The lookup is async, so the first grid fetch waits for it: otherwise the
    // unfiltered page loads first and is replaced a moment later.
    //
    // Rework after UAT: filtering to the parent PO alone was not enough. A PO
    // can carry hundreds of lines, so closing the modal still left the user
    // hunting for the line they had just come from — and if they had not
    // memorised its number, back to the chart to start again. Both the PO
    // number AND the line number are set, so the list behind the modal is the
    // one line they drilled into. The PO number stays in the panel beside it,
    // so widening back out to the sibling lines is one field away.
    const urlLine = searchParams.get('line');
    const lineDeepLink = urlLine && /^\d+$/.test(urlLine) ? parseInt(urlLine, 10) : null;
    if (lineDeepLink !== null) {
      purchasingAPI.getLine(lineDeepLink)
        .then((res) => {
          const poNumber = res.data.po_number;
          if (!poNumber) return;
          const lineNumber = res.data.line_number;
          setFilters(prev => ({
            ...prev,
            poNumber,
            // Only when the line actually reports one — a blank filter is
            // better than a filter reading "undefined" that matches nothing.
            ...(lineNumber !== null && lineNumber !== undefined
              ? { lineNumber: String(lineNumber) }
              : {}),
          }));
          // Not optional: the default tab is 'open' and a deep-linked line is
          // very often fully delivered, so filtering by PO without this would
          // show an EMPTY list — worse than the bug being fixed.
          setActiveTab('all');
          // Same affordance as every other deep link: the applied filter is
          // visible, so the user can see it and clear it to get back to everything.
          setShowFilters(true);
        })
        .catch(() => {
          // A line we cannot read falls through to the unfiltered list; the
          // modal's own error handling already tells the user about the line.
        })
        .finally(() => setInitialLoadComplete(true));
    }

    if (urlPoFilter) {
      setFilters(prev => ({
        ...prev,
        poNumber: urlPoFilter,
        lineNumber: urlLineFilter || '',
        supplier: urlSupplier || prev.supplier,
      }));
      setActiveTab('all');
      setShowFilters(true);
      // CR-5769: Keep URL params so filter persists after page refresh
    } else if (urlPartNumber) {
      // CR-6176: deep-link from the Stock Planning grid — filter to the part.
      setFilters(prev => ({ ...prev, partNumber: urlPartNumber }));
      setActiveTab('all');
      setShowFilters(true);
      // Keep the URL param so the filter persists after a page refresh.
    } else if (urlSearch) {
      setFilters(prev => ({
        ...prev,
        poNumber: urlSearch,
        supplier: urlSupplier || prev.supplier,
      }));
      setShowFilters(true);
      preserveTab();
    } else if (urlSupplier) {
      // CR-5766: Check if supplier param is a numeric ID or a name string
      // If numeric, use 'supplier' (po__supplier filter), else use 'supplierSearch' (name__icontains)
      const isNumericId = /^\d+$/.test(urlSupplier);
      if (isNumericId) {
        setFilters(prev => ({
          ...prev,
          supplier: urlSupplier,
          // CR-6310: Apply date range from Performance scorecard drill-through
          oddFrom: urlDateFrom || prev.oddFrom,
          oddTo: urlDateTo || prev.oddTo,
        }));
      } else {
        setFilters(prev => ({
          ...prev,
          supplierSearch: urlSupplier,
          // CR-6310: Apply date range from Performance scorecard drill-through
          oddFrom: urlDateFrom || prev.oddFrom,
          oddTo: urlDateTo || prev.oddTo,
        }));
      }
      // CR-5766: Set status tab if provided (e.g., 'all' to show all lines).
      // The review pill included, for a supplier too (CR-6348).
      if (urlStatusTab) {
        setActiveTab(urlStatusTab);
        if (urlStatusTab === 'otif_review' && !isOtifDecisionFilter(searchParams.get('otif_decision'))) {
          setFilters(prev => (prev.otifDecision ? prev : { ...prev, otifDecision: 'not_decided' }));
        }
      }
      setShowFilters(true);
      // CR-5766: Do NOT call preserveTab() here - keep supplier and status_tab
      // in URL so the filter persists on page refresh (same pattern as Quality.tsx)
    } else if (urlDateFrom || urlDateTo) {
      // CR-6310: Date range only (no supplier) from Performance scorecard drill-through
      setFilters(prev => ({
        ...prev,
        oddFrom: urlDateFrom || prev.oddFrom,
        oddTo: urlDateTo || prev.oddTo,
      }));
      setShowFilters(true);
    }
    // CR-7260: with a line deep link the first fetch waits for the PO lookup above.
    if (lineDeepLink === null) setInitialLoadComplete(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Digest deep links compose with the supplier scope above: merge-set so
    // neither clobbers the other, and open the filter panel so the applied
    // state is visible.
    if (urlOddStatus || urlLineStatus || customerDeepLink) {
      setFilters(prev => ({
        ...prev,
        oddStatus: urlOddStatus || prev.oddStatus,
        status: urlLineStatus || prev.status,
        customer: customerDeepLink || prev.customer,
      }));
      if (urlOddStatus || urlLineStatus) setActiveTab(urlOddStatus ? 'open' : 'all');
      setShowFilters(true);
    }

    // CR-7388: the Classic "DATE CHANGE REQUEST (Awaiting Customer Response)"
    // link is Exceptions + Status Changed + Next Action Waiting Customer, the
    // Supplier Portal preset, so every narrowing filter is one the user can
    // see. next_action sets the existing Next Action filter.
    const urlNextAction = searchParams.get('next_action');
    if (urlNextAction === 'customer' || urlNextAction === 'supplier') {
      setFilters(prev => ({ ...prev, nextAction: urlNextAction }));
      setShowFilters(true);
    }
    // status_tab used to be applied only with `supplier` (CR-5766 above).
    // Applied for a link without one too, after the line_status step so the
    // tab the link names wins over the All tab line_status selects. Not a tab
    // this tenant hides: a schedule-grain tenant has no Exceptions or OTIF
    // Review tab (CR-6518), and a link must not strand the user on one.
    const hiddenTabs = scheduleCollabEnabled ? ['needs_action', 'otif_review'] : [];
    if (urlStatusTab && !urlSupplier && !hiddenTabs.includes(urlStatusTab)) {
      setActiveTab(urlStatusTab);
      if (urlStatusTab === 'otif_review' && !isOtifDecisionFilter(searchParams.get('otif_decision'))) {
        setFilters(prev => (prev.otifDecision ? prev : { ...prev, otifDecision: 'not_decided' }));
      }
    }

    // CR-7418: the daily OTIF Review digest deep-links here now that the queue
    // lives on this grid. Same shape as the hold rows below: the filter the
    // email counted selects the pill and is carried into the query, so the page
    // opens on exactly the population the recipient clicked.
    //
    // NOTE it is THIS block that selects the pill for a digest link, not the
    // `status_tab` handling above — that only runs for a link that also
    // carries `supplier` (CR-5766), and the digest link carries no supplier.
    // Drop this and every OTIF link already sitting in an inbox lands on the
    // default tab, showing a different population from the number clicked.
    //
    // The whitelist is `isOtifDecisionFilter`, not a fourth copy of the four
    // words: an unrecognised value is ignored SILENTLY by the server
    // (`apply_decision_filter` no-ops), so a typo here would widen the grid
    // rather than fail, and no test would see it.
    //
    // A supplier follows it too: the tab, the filter and the column are theirs
    // to read (CR-6348, Aaron 5 Oct 2026).
    // CR-6518 rework: not for a schedule-grain tenant — they settle OTIF on the
    // schedule, so the queue tab is hidden for them. Supplier Performance still
    // forwards this link, and following it would strand the user on a tab with
    // no trigger in the bar and no action they are allowed to take.
    const urlOtifDecision = scheduleCollabEnabled ? null : searchParams.get('otif_decision');
    if (isOtifDecisionFilter(urlOtifDecision)) {
      const narrows = urlOtifDecision !== 'all';
      otifDecisionChosenRef.current = true;
      setFilters(prev => ({ ...prev, otifDecision: narrows ? urlOtifDecision : '' }));
      setActiveTab('otif_review');
      // Open the panel only when something IS filtered. 'all' is the control's
      // own word for "no filter", and opening the panel for it pushes the grid
      // below the fold to show a form with nothing set in it.
      if (narrows) setShowFilters(true);
    }

    // CR-7322: daily-digest split hold rows deep-link to the On Hold tab
    // narrowed to who placed the hold, so the grid shows exactly the emailed
    // population. Select the On Hold tab and carry the value into the query.
    const urlHoldPlacedBy = searchParams.get('on_hold_placed_by');
    if (urlHoldPlacedBy === 'customer' || urlHoldPlacedBy === 'supplier') {
      setFilters(prev => ({ ...prev, on_hold_placed_by: urlHoldPlacedBy }));
      setActiveTab('on_hold');
      setShowFilters(true);
    }

  }, [viewContextLoading]);

  // Fetch lines from API
  const fetchLines = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const viewParams = getViewQueryParams();
      const queryParams: Record<string, string | number | boolean> = {
        ordering: '-id',
        page: currentPage,
        page_size: pageSize,
        ...viewParams,
        ...listScopeParams,
      };

      // Only bust the browser's 60-second HTTP cache after mutations
      if (bustCacheRef.current) {
        queryParams['_t'] = Date.now();
        bustCacheRef.current = false;
      }

      // Apply filters
      if (filters.poNumber) queryParams['po__po_number__icontains'] = filters.poNumber;
      if (filters.lineNumber) queryParams['line_number'] = filters.lineNumber;
      if (filters.supplier) queryParams['po__supplier'] = filters.supplier;
      else if (filters.supplierSearch) queryParams['po__supplier__name__icontains'] = filters.supplierSearch;
      if (filters.customer) queryParams['po__customer'] = filters.customer;
      if (filters.partNumber) queryParams['part_number__icontains'] = filters.partNumber;
      if (filters.partDescription) queryParams['part_description__icontains'] = filters.partDescription;
      if (filters.description) queryParams['description__icontains'] = filters.description;
      if (filters.erpOrderType) queryParams['po__erp_order_type'] = filters.erpOrderType;
      // Sort the whole result set, not the page. Only columns backed by a real
      // database field can be ordered by the server; the computed ones
      // (pct_delivered, next_action, the converted_* currency values, the
      // contract prices and the change counts) are still sorted in the browser
      // below and therefore still only order the loaded page. That limit is
      // narrower than it was — the common columns now sort correctly across
      // pages — but it is a limit, and widening it needs queryset annotations.
      const serverSortField = sortField ? SERVER_SORTABLE[sortField] : undefined;
      if (serverSortField) {
        queryParams['ordering'] = sortDirection === 'desc' ? `-${serverSortField}` : serverSortField;
      } else if (activeTab === 'otif_review') {
        // Latest first: the most recently delivered lines are the ones still
        // worth reviewing. A column the user sorts by replaces this.
        queryParams['ordering'] = '-closed_at,po__po_number,line_number';
      }

      // CR-3413: Unified status filter.
      //
      // Status and Next Action both constrain acknowledge_status, so they are
      // resolved to sets and intersected. Sending them as separate params would
      // let the later one overwrite the earlier, which is how "Status=New +
      // Next Action=Waiting Supplier" used to return the plain Status result
      // and silently over-report. Next Action was previously filtered in the
      // browser instead, which only ever saw the loaded page.
      const statusSets: string[][] = [];
      if (filters.status) {
        if (filters.status === 'on_hold') {
          queryParams['on_hold'] = true;
        } else if (filters.status === 'closed') {
          queryParams['status'] = 'received';
        } else if (filters.status === 'changed') {
          statusSets.push(['change_proposed_supplier', 'change_proposed_customer']);
        } else if (filters.status === 'wip') {
          statusSets.push(['wip', 'accepted']);
          queryParams['on_hold'] = false;
        } else {
          statusSets.push([filters.status]);
        }
      }
      // waiting_on is derived from acknowledge_status in the serializer
      // (get_waiting_on): new / change_proposed_customer await the supplier,
      // change_proposed_supplier awaits the customer. Same mapping here so the
      // filter can be answered by the database.
      if (filters.nextAction === 'supplier') {
        statusSets.push(['new', 'change_proposed_customer']);
      } else if (filters.nextAction === 'customer') {
        statusSets.push(['change_proposed_supplier']);
      }
      if (statusSets.length) {
        const allowed = statusSets.reduce((acc, set) => acc.filter(s => set.includes(s)));
        // An empty intersection is a real answer — the two filters exclude each
        // other, so the correct result is no rows. Sending nothing would show
        // everything, which is the failure this change exists to remove.
        queryParams['acknowledge_status__in'] = allowed.length ? allowed.join(',') : '__none__';
      }
      if (filters.on_hold === 'yes') queryParams['on_hold'] = true;
      else if (filters.on_hold === 'no') queryParams['on_hold'] = false;
      // CR-7400: the selected buyer codes, matched exactly. The old
      // buyer_id__icontains returned EB10 and EB11 for a user who picked EB1.
      // A selection covering every offered code is Select All and sends
      // nothing, so the lines with no buyer code stay in (buyerIdFilterParam).
      const buyerIds = buyerIdFilterParam(filters.buyerId, filterOptions?.buyer_ids || []);
      if (buyerIds) queryParams['buyer_id__in'] = buyerIds;
      if (filters.batchSerialNo) queryParams['batch_serial_no__icontains'] = filters.batchSerialNo;
      // CR-7418: the shared server-side decision filter (otif_review.py).
      if (filters.otifDecision) queryParams['otif_decision'] = filters.otifDecision;
      // CR-7416: the measured verdict. Sent as `otif_verdict` — `otif_status`
      // is the ODD Status control's key on this same endpoint.
      if (filters.otifVerdict) queryParams['otif_verdict'] = filters.otifVerdict;
      if (filters.supplierGroup) queryParams['po__supplier__group'] = filters.supplierGroup;
      // CR-4693: users filter by what they see in the table — the ODD column
      // shows odd_date, so the range is on odd_date (server-derived, the date
      // the verdict and arrears use), not on the stored delivery_date.
      if (filters.oddFrom) queryParams['odd_date__gte'] = filters.oddFrom;
      if (filters.oddTo) queryParams['odd_date__lte'] = filters.oddTo;
      if (filters.ppv === 'positive') queryParams['ppv__gt'] = 0;
      else if (filters.ppv === 'negative') queryParams['ppv__lt'] = 0;
      else if (filters.ppv === 'zero') queryParams['ppv'] = 0;
      if (filters.commodityGroup) queryParams['commodity_group'] = filters.commodityGroup;
      if (filters.commodityType) queryParams['commodity_type'] = filters.commodityType;
      // CR-6348: reworked SO Lines filters (Company/Supplier hierarchy + legacy status)
      if (filters.companyCountry) queryParams['po__customer__country'] = filters.companyCountry;
      if (filters.division) queryParams['po__customer__group'] = filters.division;
      if (filters.supplierCountry) queryParams['po__supplier__country'] = filters.supplierCountry;
      // CR-7028: Supplier code filter via the Company's supplier_code field
      if (filters.supplierCode) queryParams['po__supplier__supplier_code__icontains'] = filters.supplierCode;
      if (filters.warehouse) queryParams['po__delivery_warehouse'] = filters.warehouse;
      if (filters.oddStatus) queryParams['otif_status'] = filters.oddStatus;
      if (filters.otdStatus) queryParams['otd_status_lspd'] = filters.otdStatus;
      if (filters.criticalOnly === 'true') queryParams['is_critical'] = true;
      // CR-6347 rework: Supplier Type + Strategic-only (shared backend helpers).
      if (filters.supplierType) queryParams['supplier_type'] = filters.supplierType;
      if (filters.strategicOnly === 'true') queryParams['strategic_only'] = true;

      // Tab-based filtering
      if (activeTab === 'open') queryParams['status__in'] = 'pending,partially_received';
      else if (activeTab === 'closed') queryParams['status'] = 'received';
      else if (activeTab === 'on_hold') queryParams['on_hold'] = true;
      else if (activeTab === 'needs_action') queryParams['needs_action'] = true;
      // CR-7418: the review queue is the server's `review_candidates` set
      // (CR-6315's shared predicate), so this pill and the row buttons can
      // never disagree about which lines may be decided.
      else if (activeTab === 'otif_review') queryParams['review_candidates'] = true;
      else Object.assign(queryParams, dueWindowParams(activeTab));

      // CR-7322: on the On Hold tab, narrow to who placed the hold when the
      // daily-digest deep link asks for it, so the grid count matches the
      // emailed count. 'customer' covers helix / NULL (backend hold_scope).
      if (activeTab === 'on_hold'
          && (filters.on_hold_placed_by === 'customer' || filters.on_hold_placed_by === 'supplier')) {
        queryParams['on_hold_placed_by'] = filters.on_hold_placed_by;
      }
      if (appliedPartSearch) queryParams['part_search'] = appliedPartSearch;

      const response = await purchasingAPI.getLines(queryParams);
      setLines(response.data.results);
      setTotalCount(response.data.count);
      // CR-7279: page count from what the server served, not what was asked for.
      const paged = pageCountFromResponse(response.data.count, pageSize, response.data.results.length, currentPage, servedPageRef.current);
      servedPageRef.current = paged.memory;
      setTotalPages(paged.totalPages);

      if (activeTab === 'needs_action') {
        setNeedsActionCount(response.data.count);
      }
      if (activeTab === 'otif_review') {
        setOtifReviewCount(response.data.count);
      }
    } catch (err: unknown) {
      console.error('Error fetching PO lines:', err);
      setError(getApiErrorMessage(err, 'Failed to load PO lines'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, activeTab, filters, appliedPartSearch, getViewQueryParams, sortField, sortDirection, listScopeParams]);

  // Fetch on page change, tab change, search trigger, or view context change
  const fetchCountRef = useRef(0);
  const prevActiveTabRef = useRef(activeTab);
  const prevPageRef = useRef(currentPage);
  const prevViewRef = useRef(`${viewLevel}-${selectedSiteId}-${selectedCompanyId}-${selectedGroupId}`);
  const prevSearchTriggerRef = useRef(searchTrigger);
  const prevPageSizeRef = useRef(pageSize);
  useEffect(() => {
    // Wait for ViewContext to resolve the active Group/Division/Site: the
    // first fetch used to go out unscoped and again with ?site= once the
    // context arrived — two full list queries for one table.
    if (!initialLoadComplete || viewContextLoading) return;

    fetchCountRef.current += 1;
    const tabChanged = prevActiveTabRef.current !== activeTab;
    const pageChanged = prevPageRef.current !== currentPage;
    const currentViewKey = `${viewLevel}-${selectedSiteId}-${selectedCompanyId}-${selectedGroupId}`;
    const viewChanged = prevViewRef.current !== currentViewKey;
    const searchTriggered = prevSearchTriggerRef.current !== searchTrigger;
    const pageSizeChanged = prevPageSizeRef.current !== pageSize;
    prevActiveTabRef.current = activeTab;
    prevPageRef.current = currentPage;
    prevViewRef.current = currentViewKey;
    prevSearchTriggerRef.current = searchTrigger;
    prevPageSizeRef.current = pageSize;

    if (fetchCountRef.current === 2 && !tabChanged && !pageChanged && !viewChanged && !searchTriggered && !pageSizeChanged) return;

    fetchLines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, activeTab, initialLoadComplete, viewContextLoading, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, searchTrigger, pageSize]);

  // CR-4597: Build filter params matching the main fetch query (shared logic)
  const buildFilterParams = useCallback(() => {
    const viewParams = getViewQueryParams();
    const params: Record<string, string | number | boolean> = {};
    Object.entries(viewParams).forEach(([k, v]) => { if (v !== undefined) params[k] = v; });
    // CR-9095: the badges count the same supplier scope as the grid.
    Object.assign(params, listScopeParams);
    if (filters.poNumber) params['po__po_number__icontains'] = filters.poNumber;
    if (filters.lineNumber) params['line_number'] = filters.lineNumber;
    if (filters.supplier) params['po__supplier'] = filters.supplier;
    else if (filters.supplierSearch) params['po__supplier__name__icontains'] = filters.supplierSearch;
    if (filters.customer) params['po__customer'] = filters.customer;
    if (filters.partNumber) params['part_number__icontains'] = filters.partNumber;
    if (filters.partDescription) params['part_description__icontains'] = filters.partDescription;
    if (filters.description) params['description__icontains'] = filters.description;
    if (filters.erpOrderType) params['po__erp_order_type'] = filters.erpOrderType;
    if (filters.on_hold === 'yes') params['on_hold'] = true;
    else if (filters.on_hold === 'no') params['on_hold'] = false;
    // CR-7400: same exact-match param as the list, through the same helper, so
    // the badges count the rows the grid shows under Select All too.
    const buyerIdsForCounts = buyerIdFilterParam(filters.buyerId, filterOptions?.buyer_ids || []);
    if (buyerIdsForCounts) params['buyer_id__in'] = buyerIdsForCounts;
    if (filters.supplierGroup) params['po__supplier__group'] = filters.supplierGroup;
    // Match the main query: the ODD range is on odd_date, what the column shows.
    if (filters.oddFrom) params['odd_date__gte'] = filters.oddFrom;
    if (filters.oddTo) params['odd_date__lte'] = filters.oddTo;
    if (filters.commodityGroup) params['commodity_group'] = filters.commodityGroup;
    if (filters.commodityType) params['commodity_type'] = filters.commodityType;
    // CR-7418: counts honour the decision filter too, or the badges would
    // describe a different row set from the list under them.
    if (filters.otifDecision) params['otif_decision'] = filters.otifDecision;
    // CR-6348: keep count queries in sync with the reworked SO Lines filters.
    if (filters.companyCountry) params['po__customer__country'] = filters.companyCountry;
    if (filters.division) params['po__customer__group'] = filters.division;
    if (filters.supplierCountry) params['po__supplier__country'] = filters.supplierCountry;
    // CR-7028: Supplier code filter via the Company's supplier_code field
    if (filters.supplierCode) params['po__supplier__supplier_code__icontains'] = filters.supplierCode;
    if (filters.warehouse) params['po__delivery_warehouse'] = filters.warehouse;
    if (filters.oddStatus) params['otif_status'] = filters.oddStatus;
    if (filters.otdStatus) params['otd_status_lspd'] = filters.otdStatus;
    if (filters.criticalOnly === 'true') params['is_critical'] = true;
    // CR-6347 rework: keep counts/export in step with the list query.
    if (filters.supplierType) params['supplier_type'] = filters.supplierType;
    if (filters.strategicOnly === 'true') params['strategic_only'] = true;
    // Counts must honour the quick search too — otherwise the badges describe
    // a different row set from the list under them.
    if (appliedPartSearch) params['part_search'] = appliedPartSearch;
    return params;
  }, [getViewQueryParams, filters, appliedPartSearch, listScopeParams]);

  // Tab badge counts — ONE batched request, not one per tab.
  //
  // This previously fired seven parallel page_size=1 list requests just to read
  // .count. Each paid the full DRF stack and they contended for the same web
  // dyno threads as the list itself, so the badges landed seconds after the
  // table and visibly lagged a filter change. The batched endpoint replays the
  // same param sets through the list's own queryset, so tab semantics cannot
  // drift from what the list shows.
  useEffect(() => {
    // Same gate as the list fetch — the counts must be scoped like the table.
    if (!initialLoadComplete || viewContextLoading) return;
    const filterParams = buildFilterParams();
    // The pill's own Not Decided default narrows the pill only; the other
    // badges keep counting their whole tab unless the user chose a decision.
    const otherParams = { ...filterParams };
    if (!otifDecisionChosenRef.current) delete otherParams['otif_decision'];
    const withFilters = (extra: Record<string, string | number | boolean>) =>
      ({ ...otherParams, ...extra });

    let cancelled = false;
    setCountsRefreshing(true);
    purchasingAPI.getPOLineTabCounts({
      needs_action: withFilters({ needs_action: true }),
      otif_review: {
        ...filterParams,
        review_candidates: true,
        // Before the pill is opened, its badge counts what it will open on.
        ...(filters.otifDecision || otifDecisionChosenRef.current ? {} : { otif_decision: 'not_decided' }),
      },
      open: withFilters({ status__in: OPEN_LINE_STATUSES }),
      closed: withFilters({ status: 'received' }),
      on_hold: withFilters({ on_hold: true }),
      due_previous_weeks: withFilters(dueWindowParams('due_previous_weeks')),
      due_this_week: withFilters(dueWindowParams('due_this_week')),
      due_next_week: withFilters(dueWindowParams('due_next_week')),
    }).then((res) => {
      // A stale response must never overwrite a newer one: filter changes can
      // fire several of these and they are not guaranteed to return in order.
      if (cancelled) return;
      const c = res.data.counts || {};
      setNeedsActionCount(c['needs_action'] ?? null);
      setOtifReviewCount(c['otif_review'] ?? null);
      setOpenCount(c['open'] ?? null);
      setClosedCount(c['closed'] ?? null);
      setOnHoldCount(c['on_hold'] ?? null);
      setDuePreviousWeeksCount(c['due_previous_weeks'] ?? null);
      setDueThisWeekCount(c['due_this_week'] ?? null);
      setDueNextWeekCount(c['due_next_week'] ?? null);
    }).catch(() => {
      // Leave the previous numbers rather than blanking every badge on a
      // transient failure — a stale count reads better than none at all.
    }).finally(() => {
      if (!cancelled) setCountsRefreshing(false);
    });

    return () => { cancelled = true; };
  }, [initialLoadComplete, viewContextLoading, buildFilterParams, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, searchTrigger, activeTab]);

  // CR-4695: Only clear view-dependent filters (customer/supplier) on site change.
  // Status, partNumber, oddFrom, oddTo and other user-selected filters are preserved.
  // CR-5766: Skip clearing on the first view context load after URL params are synced,
  // otherwise the supplier filter from URL gets cleared immediately.
  const prevViewKeyForFilters = useRef(`${viewLevel}-${selectedSiteId}-${selectedCompanyId}-${selectedGroupId}`);
  const viewContextInitialized = useRef(false);
  useEffect(() => {
    if (!initialLoadComplete) return;
    const currentKey = `${viewLevel}-${selectedSiteId}-${selectedCompanyId}-${selectedGroupId}`;
    if (prevViewKeyForFilters.current !== currentKey) {
      prevViewKeyForFilters.current = currentKey;
      // Skip clearing on the first view context change (initial load)
      // to preserve URL param filters
      if (!viewContextInitialized.current) {
        viewContextInitialized.current = true;
        return;
      }
      setFilters(prev => ({
        ...prev,
        customer: '',
        customerSearch: '',
        supplier: '',
        supplierSearch: '',
      }));
    }
  }, [viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, initialLoadComplete]);

  // Save column preferences to localStorage
  useEffect(() => {
    localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(columns));
  }, [columns]);

  // Close column dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (columnDropdownRef.current && !columnDropdownRef.current.contains(event.target as Node)) {
        setShowColumnDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch filter options for dropdowns (CR-3413)
  //
  // CR-7400: the options describe the rows on screen, so they are fetched FOR
  // THE CURRENT VIEW and refetched when it changes, exactly as the list does
  // above. Before, this ran once with empty deps and sent no view params at
  // all, so in a group or site view the Buyer ID list belonged to whichever
  // view happened to load first and could offer codes the grid cannot show
  // (or miss a sibling site's codes that it can).
  useEffect(() => {
    // Wait for the view context, like the list fetch: a request sent before
    // it resolves is scoped to the wrong thing and then cached under the
    // wrong key.
    if (viewContextLoading) return;

    // CR-9104: the options carry the same My / All suppliers scope as the
    // list. Without it the backend applied the user's buyer codes, so on All
    // suppliers the Buyer ID control still offered only the user's own codes.
    const viewParams = { ...getViewQueryParams(), ...listScopeParams };
    const allSuppliers = ALL_SUPPLIERS_PARAM in listScopeParams;
    // The cache key carries the view and the scope, so switching either cannot
    // serve the other's codes. `_v2` because the payload gained `buyer_ids`:
    // without the bump a returning user keeps a 15-minute-old payload
    // without it and the Buyer ID control has nothing to offer.
    const FILTER_CACHE_KEY =
      `po_lines_filter_options_v2:${viewLevel}-${selectedGroupId}-${selectedCompanyId}-${selectedSiteId}-${allSuppliers ? 'all' : 'mine'}`;
    const cached = Cache.get<FilterOptions>(FILTER_CACHE_KEY);
    if (cached) {
      setFilterOptions(cached);
      return;
    }
    let cancelled = false;
    const fetchFilterOptions = async () => {
      try {
        const response = await purchasingAPI.getLineFilterOptions(viewParams);
        // A stale response must never overwrite a newer one: a quick scope
        // switch fires two of these and they are not ordered.
        if (cancelled) return;
        setFilterOptions(response.data);
        Cache.set(FILTER_CACHE_KEY, response.data, 15 * 60 * 1000);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch filter options:', error);
      }
    };
    fetchFilterOptions();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewContextLoading, viewLevel, selectedGroupId, selectedCompanyId, selectedSiteId, listScopeParams]);

  // Reset page to 1 when switching tabs
  const handleTabChange = useCallback((tab: string) => {
    setCurrentPage(1);
    setActiveTab(tab);
    // Apply the pill's Not Decided default on the way in, and take it off on
    // the way out so it never narrows another tab. A decision the user picked
    // is left alone either way.
    if (!otifDecisionChosenRef.current) {
      setFilters(prev => {
        const next = tab === 'otif_review' ? 'not_decided' : '';
        return prev.otifDecision === next ? prev : { ...prev, otifDecision: next };
      });
    }
  }, []);

  // Refresh after mutations
  const refreshAfterMutation = useCallback(() => {
    bustCacheRef.current = true;
    return fetchLines();
  }, [fetchLines]);

  // Patch a single line in-place without re-fetching
  const patchLine = useCallback(async (lineId: number) => {
    try {
      const response = await purchasingAPI.getLine(lineId);
      setLines(prev => prev.map(l => l.id === lineId ? response.data : l));
    } catch (err) {
      console.error('Failed to patch line:', err);
    }
  }, []);

  // Column helpers
  const toggleColumn = useCallback((columnId: string) => {
    setColumns(prev => prev.map(col =>
      col.id === columnId ? { ...col, visible: !col.visible } : col
    ));
  }, []);

  const resetColumns = useCallback(() => {
    // CR-4716: Strip restricted columns for supplier users on reset too.
    // Also strip the call-off-only warehouse column when not applicable —
    // same gate as the initializer, so Reset Columns can't resurrect a column
    // the viewer isn't entitled to (or drop one they are).
    const base = isSupplierUser
      ? DEFAULT_COLUMNS.filter(c => !SUPPLIER_RESTRICTED_COLUMNS.includes(c.id))
      : DEFAULT_COLUMNS.filter(c => !SUPPLIER_ONLY_COLUMNS.includes(c.id));
    const callOffScoped = callOffEnabled
      ? base
      : base.filter(
          c => !CALL_OFF_SUPPLY_COLUMNS.includes(c.id)
            && !SUPPLIER_ONLY_COLUMNS.includes(c.id),
        );
    setColumns(callOffScoped);
  }, [isSupplierUser, callOffEnabled]);

  const selectAllColumns = useCallback(() => {
    setColumns(prev => prev.map(col => ({ ...col, visible: true })));
  }, []);

  const reorderColumns = useCallback((dragIndex: number, hoverIndex: number) => {
    setColumns(prev => {
      const newColumns = [...prev];
      const draggedColumn = newColumns[dragIndex];
      newColumns.splice(dragIndex, 1);
      newColumns.splice(hoverIndex, 0, draggedColumn);
      return newColumns.map((col, index) => ({ ...col, order: index }));
    });
  }, []);

  const isColumnVisible = useCallback((columnId: string): boolean => {
    const col = columns.find(c => c.id === columnId);
    return col ? col.visible : true;
  }, [columns]);

  const getColumnConfig = useCallback((columnId: string): ColumnConfig | undefined => {
    return columns.find(c => c.id === columnId);
  }, [columns]);

  // Filter handlers
  const handleFilterChange = useCallback((key: string, value: string) => {
    if (key === 'otifDecision') otifDecisionChosenRef.current = true;
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleSearch = useCallback(() => {
    setCurrentPage(1);
    setSearchTrigger(prev => prev + 1);
  }, []);

  const clearFilters = useCallback(() => {
    otifDecisionChosenRef.current = false;
    setFilters(activeTab === 'otif_review'
      ? { ...DEFAULT_FILTERS, otifDecision: 'not_decided' } : DEFAULT_FILTERS);
    setCurrentPage(1);
    // CR-7260: the grid only re-queries on Search (searchTrigger) or a page
    // change, so clearing from page 1 refreshed the tab badges and left the
    // rows behind them filtered. Clear is a search with nothing in it.
    setSearchTrigger(prev => prev + 1);
    // CR-5769: Clear filter-related URL params when user clears filters
    // (CR-9095: the supplier scope is not a filter and stays)
    setSearchParams(keptUrlParams(searchParams), { replace: true });
  }, [searchParams, setSearchParams, activeTab]);

  // Page change handler
  const handlePageChange = useCallback((newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [totalPages]);

  // Sort handlers
  const handleSort = useCallback((field: string) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    // Columns the server can order need a refetch — the sort applies to the
    // whole result set, so the first page of the new order is not the page
    // currently held. Without this the ordering param is only picked up the
    // next time something else happens to refetch. Client-sorted columns are
    // reordered in place and must not refetch, or the page would flicker.
    if (SERVER_SORTABLE[field]) {
      setCurrentPage(1);
      setSearchTrigger(prev => prev + 1);
    }
  }, [sortField]);

  // Compose sub-hooks
  const actions = usePOLinesActions({ fetchLines, setLines, bustCacheRef });
  const bulkActions = usePOLinesBulkActions({
    lines, fetchLines, bustCacheRef, otifReview: activeTab === 'otif_review',
  });
  /**
   * Quick-search keystroke handler.
   *
   * Debounced by 500ms and resets to page 1 — without the reset a term that
   * narrows the result set leaves the user stranded on a page that no longer
   * exists, showing an empty table rather than their matches.
   */
  const handleQuickPartSearch = useCallback((value: string) => {
    setPartSearch(value);
    if (partSearchTimerRef.current) clearTimeout(partSearchTimerRef.current);
    partSearchTimerRef.current = setTimeout(() => {
      setAppliedPartSearch(value.trim());
      setCurrentPage(1);
      setSearchTrigger((n) => n + 1);
    }, 500);
  }, []);

  useEffect(() => () => {
    if (partSearchTimerRef.current) clearTimeout(partSearchTimerRef.current);
  }, []);

  const exportActions = usePOLinesExport({
    filters,
    activeTab,
    partSearch: appliedPartSearch,
    // CR-7400: the download decides "all codes selected" on the same list the
    // grid does, so a Select All export matches a Select All screen.
    buyerIdOptions: filterOptions?.buyer_ids || [],
    // CR-9095: the file follows the My / All suppliers switch.
    scopeParams: exportScopeParams,
  });

  // nextAction is applied server-side now (see the acknowledge_status
  // intersection above). Filtering here as well would only re-filter the page
  // that has already been narrowed, and it was the page-local pass that made
  // the count wrong once results spilled past page 1.
  const filteredLines = lines;

  // Sort lines
  // CR-7263: this comparator used to live here inline. It now lives in
  // hooks/useColumnSort so the tables in the PO Line pop-up can use the same
  // one rather than growing another copy; the list keeps its own sort state
  // because the fetch above closes over sortField, so it calls the pure
  // comparator directly instead of taking state from the hook.
  //
  // `pct_delivered` joins the numeric list on the way past. It was derived as
  // a number and then compared as text, so a line delivered over 100% sorted
  // between 0% and 22% -- the same defect this ticket is about, one column up.
  const sortedLines = sortRows(filteredLines, sortField, sortDirection, {
    numericFields: ['quantity', 'unit_price', 'total_price', 'received_qty', 'accepted_qty', 'outstanding_qty', 'pct_delivered'],
    dateFields: ['delivery_date', 'acknowledge_date', 'on_hold_date'],
    serverSorted: SERVER_SORTABLE,
    computed: {
      // CR-7417: the comparator reads the same figure the cell renders, and
      // returns NULL when there is none. Coercing that to -1 put no-figure rows
      // first ascending and last descending — the one column in the table with
      // its own blank rule. `sortRows` already puts blanks last whichever way
      // the column points, so null gets the table's rule for free.
      pct_delivered: (l: PurchaseOrderLine) => deliveredPercentOf(l),
      next_action: (l: PurchaseOrderLine) => l.waiting_on || '',
      hold_reason: (l: PurchaseOrderLine) =>
        l.on_hold_reason_text || l.on_hold_reason_details?.description || '',
    },
  });

  return {
    // Feature flags
    scheduleCollabEnabled,

    // Data
    lines: sortedLines,
    loading,
    error,
    currentPage,
    totalPages,
    totalCount,
    needsActionCount,
    otifReviewCount,
    duePreviousWeeksCount,
    dueThisWeekCount,
    dueNextWeekCount,
    countsRefreshing,
    partSearch,
    handleQuickPartSearch,
    openCount,
    closedCount,
    onHoldCount,
    pageSize,
    // CR-7279: what the server actually serves per page for the current request size.
    servedPageSize: servedPageRef.current?.served ?? pageSize,
    handlePageSizeChange: (newSize: number) => { setPageSize(newSize); setCurrentPage(1); },
    activeTab,
    showFilters,
    user,
    // CR-9095: My suppliers / All suppliers switch
    buyerCodes: supplierScope.buyerCodes,
    mySuppliersOnly: supplierScope.mySuppliersOnly,
    setMySuppliersOnly: supplierScope.setMySuppliersOnly,

    // Selection
    selectedLineId,
    selectedLineAction,
    selectedLineForHold: actions.selectedLineForHold,
    selectedLineForWorkflow: actions.selectedLineForWorkflow,
    selectedLineIds: bulkActions.selectedLineIds,
    bulkAccepting: bulkActions.bulkAccepting,
    bulkRejecting: bulkActions.bulkRejecting,
    bulkHolding: bulkActions.bulkHolding,
    bulkProposing: bulkActions.bulkProposing,
    showBulkAcceptModal: bulkActions.showBulkAcceptModal,
    showBulkRejectModal: bulkActions.showBulkRejectModal,
    showBulkHoldModal: bulkActions.showBulkHoldModal,
    showBulkProposeModal: bulkActions.showBulkProposeModal,
    pendingAcceptIds: bulkActions.pendingAcceptIds,
    pendingAcceptVerb: bulkActions.pendingAcceptVerb,
    pendingRejectIds: bulkActions.pendingRejectIds,
    pendingHoldIds: bulkActions.pendingHoldIds,
    pendingProposeIds: bulkActions.pendingProposeIds,
    showImportModal: exportActions.showImportModal,
    // CR-6679: Order Acknowledgements import modal
    showAckImportModal: exportActions.showAckImportModal,

    // Modals
    putOnHoldModalOpen: actions.putOnHoldModalOpen,
    releaseHoldModalOpen: actions.releaseHoldModalOpen,
    acceptModalOpen: actions.acceptModalOpen,
    declineModalOpen: actions.declineModalOpen,
    holdReason: actions.holdReason,
    // G7: the hold dialog's "whose move is it" answer, and the side asking.
    holdNextAction: actions.holdNextAction,
    setHoldNextAction: actions.setHoldNextAction,
    placerSide: actions.placerSide,

    // Sort
    sortField,
    sortDirection,

    // Bulk result log
    bulkResultLog: bulkActions.bulkResultLog,
    showBulkResultLog: bulkActions.showBulkResultLog,
    setShowBulkResultLog: bulkActions.setShowBulkResultLog,

    // Export
    exporting: exportActions.exporting,

    // Filters
    filters,
    filterOptions,

    // Columns
    columns,
    showColumnDropdown,
    columnDropdownRef,

    // Setters
    setActiveTab: handleTabChange,
    setShowFilters,
    setSelectedLineId,
    setSelectedLineAction,
    setSelectedLineForHold: actions.setSelectedLineForHold,
    setSelectedLineForWorkflow: actions.setSelectedLineForWorkflow,
    setSelectedLineIds: bulkActions.setSelectedLineIds,
    setPutOnHoldModalOpen: actions.setPutOnHoldModalOpen,
    setReleaseHoldModalOpen: actions.setReleaseHoldModalOpen,
    setAcceptModalOpen: actions.setAcceptModalOpen,
    setDeclineModalOpen: actions.setDeclineModalOpen,
    setHoldReason: actions.setHoldReason,
    setShowColumnDropdown,

    // Handlers
    fetchLines,
    refreshAfterMutation,
    patchLine,
    handlePageChange,
    handleFilterChange,
    handleSearch,
    clearFilters,
    handleSort,
    toggleColumn,
    resetColumns,
    selectAllColumns,
    reorderColumns,
    isColumnVisible,
    getColumnConfig,
    handlePutOnHold: actions.handlePutOnHold,
    handleReleaseHold: actions.handleReleaseHold,
    submitPutOnHold: actions.submitPutOnHold,
    submitReleaseHold: actions.submitReleaseHold,
    handleToggleCritical: actions.handleToggleCritical,
    handleWorkflowAction: actions.handleWorkflowAction,
    handleSelectAll: bulkActions.handleSelectAll,
    handleToggleSelect: bulkActions.handleToggleSelect,
    handleBulkAccept: bulkActions.handleBulkAccept,
    handleBulkReject: bulkActions.handleBulkReject,
    handleBulkPutOnHold: bulkActions.handleBulkPutOnHold,
    handleBulkProposeChange: bulkActions.handleBulkProposeChange,
    requestBulkAccept: bulkActions.requestBulkAccept,
    requestBulkReject: bulkActions.requestBulkReject,
    requestBulkHold: bulkActions.requestBulkHold,
    requestBulkPropose: bulkActions.requestBulkPropose,
    setShowBulkAcceptModal: bulkActions.setShowBulkAcceptModal,
    setShowBulkRejectModal: bulkActions.setShowBulkRejectModal,
    setShowBulkHoldModal: bulkActions.setShowBulkHoldModal,
    setShowBulkProposeModal: bulkActions.setShowBulkProposeModal,
    setShowImportModal: exportActions.setShowImportModal,
    // CR-6679: Order Acknowledgements import modal
    setShowAckImportModal: exportActions.setShowAckImportModal,
    handleExport: exportActions.handleExport,
    getTemplateExportParams: exportActions.getTemplateExportParams,
    shouldShowActions: actions.shouldShowActions,
    shouldShowDecline: actions.shouldShowDecline,
    shouldShowProposeChange: actions.shouldShowProposeChange,
    getNextAction: actions.getNextAction,
    getSelectableLines: bulkActions.getSelectableLines,
    canSelectForBulkAccept: bulkActions.canSelectForBulkAccept,
    // CR-7418
    bulkOverridingOtif: bulkActions.bulkOverridingOtif,
    showBulkOtifModal: bulkActions.showBulkOtifModal,
    setShowBulkOtifModal: bulkActions.setShowBulkOtifModal,
    bulkOtifAction: bulkActions.bulkOtifAction,
    setBulkOtifAction: bulkActions.setBulkOtifAction,
    otifBulkProgress: bulkActions.otifBulkProgress,
    otifRetryableIds: bulkActions.otifRetryableIds,
    stopBulkOverrideOtif: bulkActions.stopBulkOverrideOtif,
    handleBulkOverrideOtif: bulkActions.handleBulkOverrideOtif,
  };
}
